import { SortDirection } from '@bp/bp-graphql-types';
import { CheckIcon, Grid, GridColumn, GridRow } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useRequireParams } from 'hooks/useRequireParams';
import { NotificationCountType } from 'matrix-js-sdk';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import { AnnouncementsList } from '../../../../components/AnnouncementsList/AnnouncementsList';
import { OpenAssignmentsOverview } from '../../../../components/Assignments/components/OpenAssignmentsOverview/OpenAssignmentsOverview';
import { BpCard } from '../../../../components/BpCard/BpCard';
import { WorkMaterialsList } from '../../../../components/WorkMaterialsList/WorkMaterialsList';
import { useGetAnnouncements } from '../../../../hooks/matrix/useGetAnnouncements';
import { useGetMatrixRoomByName } from '../../../../hooks/matrix/useGetMatrixRoomByName';
import { useDays } from '../../../../hooks/useDays';
import { useMessageCommands } from '../../../../matrix/commands/useMessageCommands';
import { niceDate } from '../../../../utils/dateCalculations';
import { RoomNames } from '../../../../utils/matrixClient';

const CourseOverviewStudentContentQueryDocument = graphql(`
  query CourseOverviewStudentContentData(
    $groupUuid: ID!
    $workMaterialsOptions: WorkMaterialOptions
    $calendarEventWhere: CalendarEventWhere!
  ) {
    ...OpenAssignmentQueryFields
    groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      teachingUnits {
        uuid
        __typename
        title
        workMaterials(options: $workMaterialsOptions) {
          uuid
          __typename
          title
          updated
          visibleFrom {
            uuid
            __typename
            date
          }
          visibleTo {
            uuid
            __typename
            date
          }
          ...WorkMaterialsListFields
        }
      }
    }
    currentCourse: groups(where: { uuid: $groupUuid, type: course }, options: { limit: 1 }) {
      uuid
      __typename
      organization {
        uuid
        __typename
      }
      subject {
        uuid
        __typename
        organization {
          uuid
          __typename
        }
      }
    }
  }
`);

export const CourseOverviewStudentContent = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const { courseUuid } = useRequireParams(['courseUuid']);
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: CourseOverviewStudentContentQueryDocument,
    variables: {
      groupUuid: courseUuid,
      workMaterialsOptions: {
        sort: [
          {
            updated: SortDirection.Asc,
          },
        ],
      },
      calendarEventWhere: courseUuid
        ? {
            holderConnection: {
              Group: {
                node: {
                  uuid: courseUuid,
                },
              },
            },
          }
        : {
            organization: {
              uuid: organizationUuid,
            },
            attendees_SOME: {
              uuid: pimAuthClaims.getProfile().uuid,
            },
          },
    },
  });

  const perms = usePermissionChecker();

  const navigate = useNavigate();

  const { markAsRead } = useMessageCommands();
  const { getRoom } = useGetMatrixRoomByName();

  const currentCourse = data?.currentCourse?.[0];

  const room = getRoom(`${currentCourse?.organization.uuid}_${courseUuid}`, RoomNames.Group);
  const hasUnread = room ? room.getRoomUnreadNotificationCount(NotificationCountType.Total) > 0 : false;
  const announcements = useGetAnnouncements(room);

  const groupSubject = {
    uuid: courseUuid ?? '',
    organization: { uuid: currentCourse?.organization.uuid ?? '' },
  };

  const materials = data?.groups
    ?.map((g) => {
      return g.teachingUnits.map((t) =>
        t.workMaterials.map((item) => {
          return {
            ...item,
            timeFrame: `${niceDate(item.visibleFrom?.date, 'short')} - ${niceDate(item.visibleTo?.date, 'short')}`,
            holder: { uuid: t.uuid, title: t.title },
          };
        }),
      );
    })
    .flat(2);

  const { daysLeft } = useDays();

  const activeMaterials = materials?.filter((a) => {
    const daysDiffVisibleTo: number = daysLeft(a.visibleTo?.date, true);
    const daysDiffVisibleFrom: number = daysLeft(a.visibleFrom?.date, true);
    return daysDiffVisibleFrom < 1 && daysDiffVisibleTo > -1;
  });

  return (
    <Grid>
      <GridRow>
        <BpCard noPadding>
          <OpenAssignmentsOverview
            courseUuid={courseUuid}
            daysToShow={5}
            onNavigate={(courseUuid, uuid) => navigate(`/courses/${courseUuid}/assignments/${uuid}`)}
            query={data}
          />
        </BpCard>
      </GridRow>
      <GridRow>
        <GridColumn width={6}>
          <BpCard
            fixedHeight='374px'
            noPadding
            header={{
              headline: t('workmaterials.title'),
            }}
          >
            <WorkMaterialsList
              onNavigate={(uuid) => navigate(`/courses/${courseUuid}/workmaterials/${uuid}`)}
              workMaterial={activeMaterials}
              isGroupEditor={currentCourse?.subject ? (perms?.isCourseEditor(currentCourse.subject) ?? false) : false}
            />
          </BpCard>
        </GridColumn>
        <GridColumn width={6}>
          <BpCard
            canScroll
            fixedHeight='374px'
            noPadding
            header={{
              headline: t('announcements.title'),
              actions: hasUnread
                ? [
                    {
                      text: t('common.mark'),
                      hierarchy: 'ghost',
                      icon: <CheckIcon />,
                      callback: async () => {
                        await markAsRead(room?.roomId);
                      },
                    },
                  ]
                : [],
            }}
          >
            <AnnouncementsList
              announcements={announcements}
              hasPermissionToDelete={perms?.canCreateCourseAnnouncement(groupSubject) ?? false}
            />
          </BpCard>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
